import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, CheckCircle2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";
import { num } from "../lib/format";

const money = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const signColor = (v) => (v == null ? "#fff" : v > 0 ? "#00FF66" : v < 0 ? "#FF3B30" : "#fff");

export default function Trades() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null); // trade id being closed
  const [exitPrice, setExitPrice] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/trades");
      setData(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doClose = async (id) => {
    if (!exitPrice) { toast.error("Enter an exit price"); return; }
    try {
      await client.post(`/trades/${id}/close`, { exit_price: Number(exitPrice) });
      toast.success("Trade closed");
      setClosing(null); setExitPrice("");
      load();
    } catch { toast.error("Could not close trade"); }
  };

  const remove = async (id) => {
    await client.delete(`/trades/${id}`);
    toast("Trade removed");
    load();
  };

  if (loading) return <div className="flex h-[70vh] items-center justify-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  const s = data.summary;
  const open = data.trades.filter((t) => t.status === "open");
  const closed = data.trades.filter((t) => t.status === "closed");

  return (
    <div className="px-4 py-5 sm:px-6">
      <div>
        <h1 className="font-head text-2xl font-black tracking-tight" data-testid="trades-title">P&amp;L DASHBOARD</h1>
        <p className="mt-1 text-sm text-gray-500">Every trade you've logged — live P&amp;L, rate of return, and results.</p>
      </div>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="pnl-summary">
        <SumCard label="Total P&L" value={money(s.total_pnl)} color={signColor(s.total_pnl)} icon={Wallet} />
        <SumCard label="Unrealized" value={money(s.unrealized_pnl)} color={signColor(s.unrealized_pnl)} />
        <SumCard label="Realized" value={money(s.realized_pnl)} color={signColor(s.realized_pnl)} />
        <SumCard label="Open Return" value={`${num(s.open_return_pct, 1)}%`} color={signColor(s.open_return_pct)} />
        <SumCard label="Win Rate" value={s.win_rate == null ? "—" : `${s.win_rate}%`} color={s.win_rate >= 50 ? "#00FF66" : "#FF7F00"} />
        <SumCard label="Avg R (closed)" value={s.avg_r == null ? "—" : `${s.avg_r}R`} color={signColor(s.avg_r)} />
      </div>

      {data.trades.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center gap-2 border border-white/10 py-24 text-center">
          <Wallet size={26} className="text-gray-700" />
          <p className="text-sm text-gray-500">No trades yet. Open a stock, size it, and tap <span className="text-white">Log This Trade</span>.</p>
          <button onClick={() => nav("/")} className="mt-2 rounded-sm border border-white/10 px-4 py-2 font-mono-sg text-xs text-gray-300 hover:border-white/20">Go to Scanner</button>
        </div>
      ) : (
        <>
          <Section title={`Open Positions (${open.length})`} rows={open} closing={closing} setClosing={setClosing}
            exitPrice={exitPrice} setExitPrice={setExitPrice} doClose={doClose} remove={remove} nav={nav} isOpen />
          <Section title={`Closed (${closed.length})`} rows={closed} remove={remove} nav={nav} />
        </>
      )}
    </div>
  );
}

function SumCard({ label, value, color, icon: Icon }) {
  return (
    <div className="border border-white/10 bg-[#111111] px-3 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="mt-1 font-mono-sg text-lg font-black" style={{ color }}>{value}</div>
    </div>
  );
}

function Section({ title, rows, closing, setClosing, exitPrice, setExitPrice, doClose, remove, nav, isOpen }) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-6">
      <h2 className="mb-2 font-head text-sm font-bold uppercase tracking-wide text-gray-400">{title}</h2>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full" data-testid={isOpen ? "open-trades-table" : "closed-trades-table"}>
          <thead className="bg-[#111111]">
            <tr className="text-left font-mono-sg text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2">Ticker</th>
              <th className="px-3 py-2">Dir</th>
              <th className="px-3 py-2 text-right">Entry</th>
              <th className="px-3 py-2 text-right">{isOpen ? "Current" : "Exit"}</th>
              <th className="px-3 py-2 text-right">Shares</th>
              <th className="px-3 py-2 text-right">P&L</th>
              <th className="px-3 py-2 text-right">Return</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2">{isOpen ? "Actions" : "Closed"}</th>
            </tr>
          </thead>
          <tbody className="font-mono-sg text-sm">
            {rows.map((t) => (
              <tr key={t.id} data-testid="trade-row" className="border-t border-white/5">
                <td className="px-3 py-2.5">
                  <button onClick={() => nav(`/stock/${t.ticker}?direction=${t.direction}`)} className="font-bold text-white hover:text-[#00F0FF]">{t.ticker}</button>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 ${t.direction === "long" ? "text-[#00FF66]" : "text-[#FF7F00]"}`}>
                    {t.direction === "long" ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{t.direction}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right text-gray-300">${num(t.entry_price)}</td>
                <td className="px-3 py-2.5 text-right text-gray-300">{t.current_price == null ? "—" : `$${num(t.current_price)}`}</td>
                <td className="px-3 py-2.5 text-right text-gray-400">{t.shares}</td>
                <td className="px-3 py-2.5 text-right font-bold" style={{ color: signColor(t.pnl) }}>{money(t.pnl)}</td>
                <td className="px-3 py-2.5 text-right" style={{ color: signColor(t.pnl_pct) }}>{t.pnl_pct == null ? "—" : `${t.pnl_pct}%`}</td>
                <td className="px-3 py-2.5 text-right" style={{ color: signColor(t.r_multiple) }}>{t.r_multiple == null ? "—" : `${t.r_multiple}R`}</td>
                <td className="px-3 py-2.5">
                  {isOpen ? (
                    closing === t.id ? (
                      <div className="flex items-center gap-1">
                        <input data-testid="exit-price-input" autoFocus type="number" placeholder="exit $" value={exitPrice}
                          onChange={(e) => setExitPrice(e.target.value)}
                          className="w-20 rounded-sm border border-white/10 bg-transparent px-2 py-1 text-xs text-white outline-none focus:border-[#00F0FF]" />
                        <button data-testid="confirm-close-btn" onClick={() => doClose(t.id)} className="rounded-sm bg-[#00FF66] px-2 py-1 text-xs font-bold text-black">✓</button>
                        <button onClick={() => { setClosing(null); setExitPrice(""); }} className="text-xs text-gray-500">✕</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button data-testid="close-trade-btn" onClick={() => { setClosing(t.id); setExitPrice(String(t.current_price ?? "")); }}
                          className="flex items-center gap-1 rounded-sm border border-white/10 px-2 py-1 text-xs text-gray-300 hover:border-[#00FF66]/40 hover:text-[#00FF66]">
                          <CheckCircle2 size={12} /> Close
                        </button>
                        <button data-testid="delete-trade-btn" onClick={() => remove(t.id)} className="text-gray-600 hover:text-[#FF3B30]"><Trash2 size={13} /></button>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-gray-500">{t.exit_date}</span>
                      <button data-testid="delete-trade-btn" onClick={() => remove(t.id)} className="text-gray-600 hover:text-[#FF3B30]"><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
